import { DateRangePicker } from "@bjarkehs/react-nice-dates";
import { faFilter, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Grid, Select, Stack, Text, TextInput } from "@mantine/core";
import { enAU } from "date-fns/locale";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CommunicationMechanism, CommunicationType } from "../../../data/api/adminCommunicationApi";

interface CommunicationsFilterProps {
  filters: any;
  onFiltersChange: (filters: any) => void;
  loading: boolean;
}

const CommunicationsFilter: React.FunctionComponent<CommunicationsFilterProps> = ({
  filters,
  onFiltersChange,
  loading,
}) => {
  const [localFilters, setLocalFilters] = useState({
    mechanism: filters.mechanism || '',
    communicationType: filters.communicationType || '',
    startDate: filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : '',
    endDate: filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : '',
    search: filters.search || '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedSearch = useCallback((searchValue: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const newFilters = { ...localFilters, search: searchValue };
      applyFilters(newFilters);
    }, 500); // 500ms debounce
  }, [localFilters]);

  const handleInputChange = (field: string, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));

    if (field === "search") {
      debouncedSearch(value);
    }
  };

  const handleDateChange = (field: "startDate" | "endDate") => (date: Date | null) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: date ? DateTime.fromJSDate(date).toISODate() : ""
    }));
  };

  const applyFilters = (filtersToApply = localFilters) => {
    const cleanedFilters: any = {};

    if (filtersToApply.mechanism) {
      cleanedFilters.mechanism = parseInt(filtersToApply.mechanism);
    }

    if (filtersToApply.communicationType) {
      cleanedFilters.communicationType = filtersToApply.communicationType;
    }

    if (filtersToApply.startDate) {
      cleanedFilters.startDate = new Date(filtersToApply.startDate);
    }

    if (filtersToApply.endDate) {
      cleanedFilters.endDate = new Date(filtersToApply.endDate);
    }

    if (filtersToApply.search) {
      cleanedFilters.search = filtersToApply.search;
    }

    onFiltersChange(cleanedFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      mechanism: '',
      communicationType: '',
      startDate: '',
      endDate: '',
      search: '',
    };
    setLocalFilters(emptyFilters);
    onFiltersChange({});
  };

  const hasActiveFilters = Object.values(localFilters).some(value => value !== '');

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Button
          color="gray"
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faFilter} className="me-1" />
          Filters
          {hasActiveFilters && (
            <span className="badge bg-primary ms-1">
              {Object.values(localFilters).filter(v => v !== '').length}
            </span>
          )}
        </Button>

        {hasActiveFilters && (
          <Button
            color="red"
            variant="outline"
            size="sm"
            onClick={clearFilters}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Clear
          </Button>
        )}
      </div>

      <Stack gap="sm" className="mb-3">
        <TextInput
          type="text"
          placeholder="Search communications..."
          value={localFilters.search}
          onChange={(e) => handleInputChange('search', e.target.value)}
          disabled={loading}
        />
      </Stack>

      {showFilters && (
        <Stack gap="md" className="mb-3 p-3 border rounded">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="sm">
                <label className="fw-500" htmlFor="mechanismFilter">Mechanism</label>
                <Select
                  id="mechanismFilter"
                  value={localFilters.mechanism}
                  onChange={(value) => handleInputChange('mechanism', value)}
                  disabled={loading}
                  data={[
                    { value: "", label: "All Mechanisms" },
                    { value: CommunicationMechanism.email.toString(), label: "Email" },
                    { value: CommunicationMechanism.sms.toString(), label: "SMS" },
                    { value: CommunicationMechanism.notification.toString(), label: "Push Notification" }
                  ]}
                />
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="sm">
                <label className="fw-500" htmlFor="communicationTypeFilter">Communication Type</label>
                <Select
                  id="communicationTypeFilter"
                  value={localFilters.communicationType}
                  onChange={(value) => handleInputChange('communicationType', value)}
                  disabled={loading}
                  data={[
                    { value: "", label: "All Types" },
                    { value: CommunicationType.Rostered, label: "Rostered" },
                    { value: CommunicationType.Unrostered, label: "Unrostered" },
                    { value: CommunicationType.BankDetailsChanged, label: "Bank Details Changed" },
                    { value: CommunicationType.PaymentNotification, label: "Payment Notification" }
                  ]}
                />
              </Stack>
            </Grid.Col>
          </Grid>

          <div className="nice-dates-compact">
            <DateRangePicker
              startDate={localFilters.startDate ? DateTime.fromISO(localFilters.startDate).toJSDate() : undefined}
              endDate={localFilters.endDate ? DateTime.fromISO(localFilters.endDate).toJSDate() : undefined}
              onStartDateChange={handleDateChange("startDate")}
              onEndDateChange={handleDateChange("endDate")}
              locale={enAU}
              format="yyyy-MM-dd"
            >
              {({ startDateInputProps, endDateInputProps, focus }) => (
                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap="sm">
                      <label className="fw-500" htmlFor="startDateFilter">Start Date (YYYY-MM-DD)</label>
                      <TextInput
                        {...startDateInputProps}
                        id="startDateFilter"
                        type="text"
                        placeholder="YYYY-MM-DD"
                        disabled={loading}
                        className={`input${focus === 'startDate' ? ' -focused' : ''}`}
                      />
                    </Stack>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap="sm">
                      <label className="fw-500" htmlFor="endDateFilter">End Date (YYYY-MM-DD)</label>
                      <TextInput
                        {...endDateInputProps}
                        id="endDateFilter"
                        type="text"
                        placeholder="YYYY-MM-DD"
                        disabled={loading}
                        className={`input${focus === 'endDate' ? ' -focused' : ''}`}
                      />
                    </Stack>
                  </Grid.Col>
                </Grid>
              )}
            </DateRangePicker>
          </div>

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }} className="d-flex align-items-end">
              <Button
                color="blue"
                variant="filled"
                onClick={() => applyFilters()}
                disabled={loading}
                className="me-2"
              >
                Apply Filters
              </Button>
              <Button
                color="gray"
                variant="outline"
                onClick={() => setShowFilters(false)}
                disabled={loading}
              >
                Hide
              </Button>
            </Grid.Col>
          </Grid>
        </Stack>
      )}
    </div>
  );
};

export default CommunicationsFilter;
